// ============================================================================
//  ClaimsErrorReport.js — the month's errors, as a report you can hand over
// ----------------------------------------------------------------------------
//  "At the end of the editing of every report, we can have an overview of what
//  transpired… the topic at the top, then in a small table the number of claims,
//  then the individual details in a table form, and the error rate. Very
//  professional, with a little bit of detail but not so much."
//
//  So: three layers, in the order somebody reads them.
//
//    1. THE OVERVIEW.  How many claims, how many NHIA will reject, the error rate.
//                      One glance, no scrolling. This is the answer to "how did
//                      this month go".
//    2. THE SUMMARY.   One row per rule that fired, worst first. This is the
//                      answer to "what is wrong with it".
//    3. THE DETAIL.    Under each rule, the claims themselves — folder number,
//                      membership number, patient, service. This is the answer to
//                      "which ones", and it is the part somebody actually works
//                      from.
//
//  ── IT COMPUTES NOTHING THE TABLE DOES NOT ALREADY KNOW ─────────────────────
//
//  Deliberately no endpoint of its own. Every claim on the screen behind this
//  already carries `issues` — the keys of the rules it breaks, from
//  claimRules.ISSUE_SQL — and `issueTypes` carries their titles and severities.
//  This component groups what is already loaded.
//
//  That is not laziness, it is the point: a report served by a second query would
//  eventually disagree with the table it is printed from, and the officer would
//  have no way to tell which was right. A report is only worth handing over if it
//  says the same thing as the screen.
//
//  ── WHAT IT REFUSES TO IMPLY ────────────────────────────────────────────────
//
//  `issues_checked` is FALSE on a typed-in claim: the rule predicates read the
//  normalised claim tables, and a typed-in claim keeps its diagnoses elsewhere. An
//  empty `issues` array there means NOT CHECKED, not "clean".
//
//  So the counts and the error rate are over the CHECKED claims only, and the
//  unchecked ones are stated as their own figure. Folding them in as clean would
//  flatter every month that mixes the two — which is most of them.
// ============================================================================

import React, { useMemo, useState } from 'react';
import {
  X, Printer, FileSpreadsheet, AlertTriangle, AlertCircle, CheckCircle2,
  HelpCircle, Loader2,
  //  The mark for a GAP — something the claim does not carry. Deliberately not a
  //  warning triangle: these claims are not suspected of anything.
  FileText,
} from 'lucide-react';

import { BRAND } from '../../brand';
import { PRODUCT } from '../../brand';

//  How many claims are listed per rule ON SCREEN and in print. The Excel export
//  always carries every one.
//
//  A cap exists because a rule can be near-universal: one live month has 1,095
//  claims with no patient name, and rendering four thousand table rows inside a
//  dialog is how the browser stops responding. The omitted count is always shown —
//  a truncated report that does not say it is truncated is worse than no report.
const DETAIL_CAP = 250;

const NBSP = ' ';
const pct = (n, of) => (of ? `${((n / of) * 100).toFixed(1)}%` : '—');
const num = (n) => Number(n || 0).toLocaleString();

//  ── THE DETAIL COLUMNS ──────────────────────────────────────────────────────
//  Declared once and used by the table, the print layout and the spreadsheet, so
//  the printout and the export cannot show different columns from the screen.
const COLUMNS = [
  { key: 'folder_no', head: 'Folder no', width: 20 },
  { key: 'member_no', head: 'Member no', width: 14 },
  { key: 'ccc', head: 'CCC', width: 10 },
  { key: 'name', head: 'Patient', width: 26 },
  { key: 'dob', head: 'Date of birth', width: 13 },
  { key: 'gender', head: 'Gender', width: 9 },
  { key: 'type', head: 'Type', width: 7 },
  { key: 'attendance', head: 'Specialty', width: 11 },
  { key: 'service', head: 'Service', width: 30 },
  { key: 'visit_date', head: 'Visit', width: 12 },
  { key: 'discharge_date', head: 'Discharge', width: 12 },
];

const cell = (c, key) => {
  const v = c[key];
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
};

// ── THE ARITHMETIC ──────────────────────────────────────────────────────────
//
//  A pure function, outside the component and exported, because these are the
//  numbers somebody quotes in a meeting and "it looked right on screen" is not a
//  check. tests/claim-error-report.test.js runs this directly.
//
//  ── THE DENOMINATOR IS THE CHECKED CLAIMS, NOT THE MONTH ──────────────────
//  The one decision here that can quietly flatter a facility. `issues_checked` is
//  false on a typed-in claim — the rule predicates read the normalised tables, and
//  a typed-in claim keeps its diagnoses in doctors_analysis — so its empty `issues`
//  array means NOT CHECKED. Counting those as clean would drop the error rate of
//  every month that mixes the two claim models, which is most of them.
//  One word per severity, used by the screen's chip and by both Excel sheets, so
//  the printout and the spreadsheet cannot end up calling the same thing two
//  different things.
export const SEV_WORD = (sev) => (
  sev === 'error' ? 'Rejects'
    : sev === 'gap' ? 'Missing'
      //  The third severity. Counted and shown, but neither a rejection nor a
      //  query — see the note block in summarise() for why it had to exist.
      : sev === 'note' ? 'Note'
        : 'Check');


export function summarise(claims = [], issueTypes = [], gaps = null, gapTypes = []) {
  const checked = claims.filter((c) => c.issues_checked);

  //  ── WHICH BAND A SEVERITY SORTS INTO ──────────────────────────────────────
  //  Errors first because NHIA rejects them, then the queries, then the notes —
  //  and the claim COUNT is only ever a tie-break INSIDE a band. Without this a
  //  note on 1,900 claims sorts above a rejection on 2, which is the table
  //  putting the wrong thing in front of the officer first.
  //
  //  DECLARED IN HERE, not at module level, and that is not a style choice.
  //  tests/claim-error-report.test.js lifts this function's source out of the file
  //  and evaluates it on its own — that is how a frontend module is exercised
  //  without a bundler. A module-level helper is not lifted with it, so the whole
  //  suite died with "SEV_RANK is not defined" the moment this was declared
  //  outside. `summarise` has to stay self-contained to stay testable.
  const rank = (sev) => (sev === 'error' ? 0 : sev === 'note' ? 2 : 1);

  const errorCodes = new Set(
    issueTypes.filter((t) => t.severity === 'error').map((t) => t.code)
  );
  //  ── THE THIRD SEVERITY ────────────────────────────────────────────────────
  //
  //  A 'note' is counted and shown in the table with its own total and share, and
  //  it is NEITHER a rejection nor a query. It exists for one shape of finding:
  //  true of a large fraction of a batch, and not wrong.
  //
  //  "No medication — out-patient" is the case it was added for. It is ~28% of a
  //  batch and every one of those claims is fine — an OPD consultation that
  //  dispensed no medicine is an ordinary claim. Counted as a warning it would
  //  have taken "nothing against them" from 4,433 to about 2,500 and pushed
  //  "worth checking" from 877 to 2,800, on a batch where not one claim had
  //  changed. Both are numbers an officer acts on.
  //
  //  So a note does not disqualify a claim from being clean, and does not put it
  //  in `warnOnly`. Everything the report says about RISK is unaffected by adding
  //  one; only the table gains a row.
  const noteCodes = new Set(
    issueTypes.filter((t) => t.severity === 'note').map((t) => t.code)
  );
  const isError = (c) => (c.issues || []).some((k) => errorCodes.has(k));
  //  A real finding: anything that is not a note. An unknown severity counts as
  //  one — a rule whose severity this build does not recognise is reported rather
  //  than silently discounted.
  const isJudged = (c) => (c.issues || []).some((k) => !noteCodes.has(k));

  const withErrors = checked.filter(isError);
  const warnOnly = checked.filter((c) => isJudged(c) && !isError(c));
  const clean = checked.filter((c) => !isJudged(c));
  //  Shown as its own figure so the claims carrying only notes are not invisible:
  //  they are inside `clean`, which is correct, and worth being able to name.
  const noteOnly = checked.filter((c) => !isJudged(c) && (c.issues || []).length);

  //  One entry per rule that ACTUALLY FIRED. A rule with nothing against it is
  //  left out entirely rather than listed as zero: this is a report on what is
  //  wrong, and thirty "0" rows bury the four that matter.
  const rules = issueTypes
    .map((t) => ({
      ...t,
      rows: checked.filter((c) => (c.issues || []).includes(t.code)),
    }))
    .filter((r) => r.rows.length > 0)
    .sort((a, b) => (
      //  THREE BANDS, in the order the officer needs them: NHIA rejects the first,
      //  queries the second, and the third is context. A note carrying 1,900
      //  claims must not sort above a rejection carrying 2 — the count is the
      //  tie-break WITHIN a band, never across them.
      rank(a.severity) - rank(b.severity)
      || b.rows.length - a.rows.length
      || String(a.name).localeCompare(String(b.name))
    ));

  //  ── WHAT IS MISSING FROM THE WHOLE BATCH ──────────────────────────────────
  //
  //  "We need more information pertaining to what is wrong with this month's
  //  claims."
  //
  //  Some faults are not per-claim at all. Measured on May 2026:
  //
  //      no claim carries an investigation      16,142 of 16,142
  //      no claim names a physician             16,142 of 16,142
  //      no claim carries a priced amount       16,138 of 16,142
  //
  //  Each of those is ONE fact about the import, and June and July do carry
  //  investigations — so May's absence is real and worth knowing. But writing them
  //  as rules would add 48,000 findings to a table of 1,159 and bury every one of
  //  them. A rule that fires on 100% of the month is not a finding about a claim.
  //
  //  So they are counted here and stated as sentences. The threshold is 90%: below
  //  that it is a per-claim problem and belongs in the table above; at or above it,
  //  the batch is missing something wholesale and one line says it better than
  //  thousands of rows.
  //  Counted by the server over the same scope the table was filtered by, and
  //  passed in — `gaps` from the claims list's stats. Not recomputed here: an
  //  investigation line is not on the claim row, and putting it there would be a
  //  correlated EXISTS on every row of the month to answer three questions.
  //
  //  `gaps` is null on an installation whose model cannot answer this, and the
  //  section then says nothing at all — which is right. "Nothing is missing" and
  //  "we could not look" are different statements and only one of them is safe.
  const WHOLESALE_AT = 0.9;
  const over = (n) => gaps && gaps.counted && (n / gaps.counted) >= WHOLESALE_AT;
  const batchGaps = !gaps ? [] : [
    over(gaps.noInvestigation) && {
      key: 'no_investigations',
      n: gaps.noInvestigation,
      of: gaps.counted,
      say: 'No claim in this batch carries a laboratory or scan line.',
      why: 'Other months in this database do carry them, so this is worth checking '
         + 'against the source file before submitting — an investigation that was '
         + 'done and not billed is money the facility has spent and not claimed.',
    },
    over(gaps.noAmount) && {
      key: 'no_amount',
      n: gaps.noAmount,
      of: gaps.counted,
      say: 'No claim in this batch carries a priced amount.',
      why: 'Every diagnosis and procedure line is stored at a tariff of 0, so the '
         + 'counts on this report describe work and not money.',
    },
  ].filter(Boolean);

  //  ── WHAT THE CLAIMS ARE MISSING, PER CLAIM ────────────────────────────────
  //
  //  THE HOLE THIS FILLS. An officer filtered the month to "No medication · 735
  //  claims", opened this report, and found no mention of it anywhere. The report
  //  is built from what the rows carry, and rows carried only `issues` — a GAP was
  //  a search predicate and a number on a tile, nothing more. The tile said 735
  //  and the report said nothing, about the same claims, on the same screen.
  //
  //  ── AND THEY ARE NOT ERRORS ─────────────────────────────────────────────
  //  Kept out of `rules`, out of `findings` and out of `errorRate`, all
  //  deliberately. An out-patient consultation with no medicine is an ordinary
  //  claim; there are 735 of them here against 3 real errors. Folded in, the rate
  //  goes from 0.4% to 100% and the one number an officer acts on becomes
  //  meaningless.
  //
  //  Same shape as `rules` so the table renders with the same code, and only
  //  those that actually fired — a report on what is missing has no use for
  //  twenty "0" rows.
  //  ── A SPLIT CHECK IS READ AS A PAIR, SO IT IS SORTED AS ONE ───────────────
  //
  //  "No medication" is now three checks — in-patient, out-patient, and the claims
  //  whose attendance cannot be read — and the whole point of splitting it was to
  //  read the halves against each other:
  //
  //      "So all the in-patient without medication is listed there with the
  //       percentage and then the total. And then no medication — out-patient, you
  //       list all those ones so we know the difference."
  //
  //  A plain count sort puts them at opposite ends of the table: out-patient is
  //  ~550 claims and in-patient ~7, so the comparison the officer asked for would
  //  span nine rows. The family therefore sorts as a unit — placed where its
  //  LARGEST member would have gone, then ordered within itself — so the halves
  //  are always adjacent and still land among rows of a comparable size.
  const familyOf = (code) => String(code || '')
    .replace(/_(ipd|opd|attendance_unknown)$/, '');

  const gapHits = (gapTypes || [])
    .map((t) => ({
      ...t,
      severity: 'gap',
      family: familyOf(t.code),
      rows: checked.filter((c) => (c.gaps || []).includes(t.code)),
    }))
    .filter((r) => r.rows.length > 0);

  const familyPeak = new Map();
  for (const r of gapHits) {
    familyPeak.set(r.family, Math.max(familyPeak.get(r.family) || 0, r.rows.length));
  }

  const gapRules = gapHits.sort((a, b) => (
    (familyPeak.get(b.family) || 0) - (familyPeak.get(a.family) || 0)
    || String(a.family).localeCompare(String(b.family))
    || b.rows.length - a.rows.length
    || String(a.name).localeCompare(String(b.name))
  ));

  //  ── DID THE COMPLETENESS CHECKS RUN AT ALL? ───────────────────────────────
  //
  //  An empty `gapRules` has two causes and they are opposite news: every claim
  //  carries everything, or nobody looked. The section renders only when
  //  something fired, so both used to produce the same screen — no section — and
  //  a batch whose checks had failed read as complete.
  //
  //  THAT IS THE REPORT THE FACILITY SENT BACK. Its per-claim section was absent
  //  while the table above it said six claims have no diagnosis. Those six satisfy
  //  `no_diagnosis` by definition, so the section had rows and showed none:
  //  claimGapFlags had caught an error and returned an empty Map, which every
  //  caller then read as an all-clear.
  //
  //  `gaps_checked` is the row-level answer, the twin of `issues_checked`. Absent
  //  on an older payload, so `undefined` is treated as "checked" — that keeps a
  //  frontend deployed ahead of its backend behaving exactly as it did rather than
  //  announcing a failure it cannot actually see.
  const gapsChecked = !checked.length
    || checked.some((c) => c.gaps_checked === undefined || c.gaps_checked);

  return {
    total: claims.length,
    checked: checked.length,
    unchecked: claims.length - checked.length,
    //  Whole-batch gaps, kept apart from `rules` on purpose — see above.
    batchGaps,
    //  Per-claim gaps: what each claim is MISSING, as opposed to what it gets
    //  wrong. Its own list, its own count, and absent from the error rate.
    gapRules,
    gapFindings: gapRules.reduce((n, r) => n + r.rows.length, 0),
    withAnyGap: checked.filter((c) => (c.gaps || []).length).length,
    //  False means the completeness checks could not be run on this batch, which
    //  is NOT the same as finding nothing. See above.
    gapsChecked,
    withErrors: withErrors.length,
    warnOnly: warnOnly.length,
    clean: clean.length,
    //  Inside `clean`, and named so the row can be explained: these carry a note
    //  and nothing else. Reported rather than hidden — an officer who sees a row
    //  for 1,900 claims and a "nothing against them" figure that did not move is
    //  entitled to know both are true.
    noteOnly: noteOnly.length,
    errorRate: checked.length ? withErrors.length / checked.length : null,
    //  Rule hits, not claims. A claim breaking three rules contributes three —
    //  which is the number that describes the WORK, while `withErrors` describes
    //  the risk. Both are shown, because they answer different questions and
    //  quoting one for the other overstates or understates the month.
    //  NOTES EXCLUDED. The tile reads "findings across all rules" and an officer
    //  reads it as "how much work is in this batch". Folding in ~1,900 ordinary
    //  out-patient consultations takes 3,923 to about 5,800 and describes no extra
    //  work at all. Counted separately below.
    findings: rules.filter((r) => r.severity !== 'note')
      .reduce((n, r) => n + r.rows.length, 0),
    noteFindings: rules.filter((r) => r.severity === 'note')
      .reduce((n, r) => n + r.rows.length, 0),
    rules,
    opd: claims.filter((c) => c.type === 'OPD').length,
    ipd: claims.filter((c) => c.type === 'IPD').length,
  };
}

export default function ClaimsErrorReport({
  open,
  onClose,
  //  The claims AS FILTERED ON SCREEN, not the whole month. A report printed
  //  under a date range has to describe that range, or it is a different
  //  document from the one the officer was looking at.
  claims = [],
  issueTypes = [],
  //  What the report is OF: the words already in the screen's own heading, passed
  //  in rather than re-derived, so the printout and the screen agree on their
  //  subject.
  scope = {},
  //  `stats.gaps` from the claims list — what the WHOLE batch is missing, counted
  //  by the server over the same scope. Null where the installation's model cannot
  //  answer it, and the section is then omitted rather than claiming all is well.
  gaps = null,
  //  The gap catalogue — title per key — the same way issueTypes carries the rule
  //  titles. From the server, so this holds no second copy of either.
  gapTypes = [],
}) {
  const [busy, setBusy] = useState(false);
  const [xlsErr, setXlsErr] = useState(null);

  const model = useMemo(
    () => summarise(claims, issueTypes, gaps, gapTypes),
    [claims, issueTypes, gaps, gapTypes]
  );

  if (!open) return null;

  const title = [
    scope.facility,
    scope.period,
    scope.scheme,
  ].filter(Boolean).join('  ·  ');

  const generated = new Date();
  const stamp = generated.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  // ── EXCEL ─────────────────────────────────────────────────────────────────
  //  Three sheets, because they get used differently: the Overview is read, the
  //  Summary is presented, and the Details are FILTERED AND PIVOTED — which is why
  //  it is one flat sheet with the rule as a column rather than a sheet per rule.
  //  A workbook with fourteen tabs cannot be pivoted at all.
  const exportExcel = async () => {
    setBusy(true);
    setXlsErr(null);
    try {
      const mod = await import('exceljs');
      const ExcelJS = mod.default || mod;
      const wb = new ExcelJS.Workbook();
      wb.creator = `${PRODUCT.vendor} ${PRODUCT.name}`;
      wb.created = generated;

      const NAVY = 'FF123A6D';
      const headRow = (ws) => {
        const r = ws.getRow(1);
        r.height = 22;
        r.eachCell((c) => {
          c.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
          c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
          c.alignment = { vertical: 'middle', horizontal: 'left' };
        });
      };

      // ── Overview ──
      const ov = wb.addWorksheet('Overview');
      ov.columns = [{ width: 34 }, { width: 46 }];
      ov.addRow(['Claim Error Report', '']);
      ov.getRow(1).font = { bold: true, size: 15, color: { argb: NAVY } };
      ov.addRow(['', '']);
      for (const [k, v] of [
        ['Facility', scope.facility || '—'],
        ['Period', scope.period || '—'],
        ['Scheme', scope.scheme || '—'],
        ['Narrowed to', scope.narrowing || 'every claim in the period'],
        ['Visit dates', scope.dates || 'the whole period'],
        ['Search in force', scope.search || 'none'],
        ['Generated', stamp],
        ['', ''],
        ['Claims in scope', model.total],
        ['  of which out-patient', model.opd],
        ['  of which in-patient', model.ipd],
        ['', ''],
        ['Claims checked against the rules', model.checked],
        ['Typed-in claims NOT checked', model.unchecked],
        ['', ''],
        ['Claims NHIA will reject (one or more errors)', model.withErrors],
        ['Claims with warnings only', model.warnOnly],
        ['Claims with nothing against them', model.clean],
        ['Total findings (a claim may break several rules)', model.findings],
        ['', ''],
        //  ── THE COMPLETENESS TOTALS, KEPT APART FROM THE RULE TOTALS ────────
        //  Asked for alongside the split of "no medication" into its in-patient
        //  and out-patient halves. NOT added to the findings figure above and not
        //  in the error rate: NHIA does not reject a claim for these, and an
        //  out-patient consultation with no medicine is an ordinary claim.
        //
        //  Two numbers, because the table's Claims column does not add up to a
        //  claim count — a claim missing two things is in two rows.
        ['Claims missing something (not errors, not rejected)',
          model.gapsChecked ? model.withAnyGap : 'not checked'],
        ['  entries in the "what is missing" table',
          model.gapsChecked ? model.gapFindings : 'not checked'],
        ['', ''],
        ['ERROR RATE (rejectable ÷ checked)',
          model.errorRate == null ? '—' : pct(model.withErrors, model.checked)],
      ]) ov.addRow([k, v]);
      ov.getColumn(1).font = { bold: false };
      ov.getRow(ov.rowCount).font = { bold: true, size: 12, color: { argb: 'FFD64545' } };
      //  The one caveat that changes how every number above is read.
      ov.addRow(['', '']);
      ov.addRow(['Note', 'The rates are over the CHECKED claims. A typed-in claim '
        + 'is not checked by these rules — its diagnoses live in different tables — '
        + 'so it is counted separately and never as clean.']);
      ov.getRow(ov.rowCount).getCell(2).alignment = { wrapText: true, vertical: 'top' };
      ov.getRow(ov.rowCount).height = 44;

      // ── Summary ──
      const sm = wb.addWorksheet('Summary');
      sm.columns = [
        { header: 'What is wrong', key: 'name', width: 52 },
        { header: 'Severity', key: 'sev', width: 12 },
        { header: 'Claims', key: 'n', width: 10 },
        { header: 'Share of checked', key: 'share', width: 17 },
      ];
      headRow(sm);
      //  The gaps go in the SAME sheet, after the rules, with their own severity
      //  word. A spreadsheet the officer filters and sorts is the wrong place to
      //  split one month's findings across two tabs — and `Missing` in the
      //  severity column is all the separation the filter needs.
      for (const r of [...model.rules, ...model.gapRules]) {
        sm.addRow({
          name: r.name,
          sev: SEV_WORD(r.severity),
          n: r.rows.length,
          share: model.checked ? r.rows.length / model.checked : 0,
        });
      }
      sm.getColumn('share').numFmt = '0.0%';
      sm.getColumn('n').alignment = { horizontal: 'right' };
      sm.views = [{ state: 'frozen', ySplit: 1 }];

      // ── Details ──
      //  EVERY row, with no cap. The cap on screen exists so a dialog stays
      //  responsive; a spreadsheet has no such problem and this is the sheet
      //  somebody actually works the month from.
      const dt = wb.addWorksheet('Details');
      dt.columns = [
        { header: 'What is wrong', key: 'rule', width: 46 },
        { header: 'Severity', key: 'sev', width: 11 },
        ...COLUMNS.map((c) => ({ header: c.head, key: c.key, width: c.width })),
      ];
      headRow(dt);
      for (const r of [...model.rules, ...model.gapRules]) {
        for (const c of r.rows) {
          dt.addRow({
            rule: r.name,
            sev: SEV_WORD(r.severity),
            ...Object.fromEntries(COLUMNS.map((col) => [col.key, cell(c, col.key)])),
          });
        }
      }
      //  Text format on the identifier columns. A membership number is digits but
      //  it is NOT a quantity: Excel drops the leading zero off 0412... and the
      //  number no longer matches the card.
      for (const key of ['folder_no', 'member_no', 'ccc']) dt.getColumn(key).numFmt = '@';
      dt.views = [{ state: 'frozen', ySplit: 1 }];
      dt.autoFilter = { from: 'A1', to: { row: 1, column: dt.columns.length } };

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Claim-errors ${[scope.facility, scope.period].filter(Boolean)
        .join(' ')}`.replace(/[\\/:*?"<>|]+/g, '-').trim() + '.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      //  Revoked on a later tick: Safari has not begun reading the blob when
      //  click() returns.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setXlsErr(e?.message || 'The spreadsheet could not be built.');
    } finally {
      setBusy(false);
    }
  };

  //  THREE, not two. A GAP is neither a rejection nor a warning — it is something
  //  the claim does not carry — and giving it the amber CHECK badge put 735
  //  ordinary out-patient consultations in the same visual class as three claims
  //  NHIA will actually send back. Its own quiet grey, so it reads as information
  //  rather than as an accusation.
  const sevChip = (sev) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '1px 7px', borderRadius: 20, fontSize: 10,
      fontWeight: 800, whiteSpace: 'nowrap',
      //  A NOTE WEARS THE SAME QUIET GREY AS A GAP, and for the reason the
      //  comment above gives about gaps: putting 1,900 ordinary out-patient
      //  consultations in amber beside ten claims NHIA will send back reads as an
      //  accusation. It is information, and it is coloured as information.
      background: sev === 'error' ? BRAND.dangerSoft
        : (sev === 'gap' || sev === 'note') ? BRAND.head : BRAND.amberSoft,
      color: sev === 'error' ? BRAND.danger
        : (sev === 'gap' || sev === 'note') ? BRAND.inkMuted : BRAND.amber,
      border: `1px solid ${sev === 'error' ? `${BRAND.danger}44`
        : (sev === 'gap' || sev === 'note') ? BRAND.line : BRAND.amberLine}`,
    }}>
      {sev === 'error' ? 'REJECTS'
        : sev === 'gap' ? 'MISSING'
          : sev === 'note' ? 'NOTE' : 'CHECK'}
    </span>
  );

  const th = {
    textAlign: 'left', padding: '6px 8px', fontSize: 9.5, fontWeight: 800,
    letterSpacing: '.04em', textTransform: 'uppercase', color: BRAND.inkMuted,
    background: BRAND.head, borderBottom: `1px solid ${BRAND.line}`,
    whiteSpace: 'nowrap',
  };
  const td = {
    padding: '5px 8px', fontSize: 11, color: BRAND.inkSoft,
    borderBottom: `1px solid ${BRAND.line}`, whiteSpace: 'nowrap',
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Claim error report"
      style={{
        position: 'fixed', inset: 0, zIndex: 1400, display: 'grid',
        placeItems: 'start center', padding: '3vh 16px',
        background: 'rgba(15,31,61,.55)', overflowY: 'auto',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>

      {/*  ── THE PRINT RULES ─────────────────────────────────────────────────
           Everything on the page is hidden and the report alone is shown, rather
           than opening a second window: a new window would need this styling
           inlined into it, which is a second copy of the design that goes stale.

           LANDSCAPE, because the detail table carries eleven columns. In portrait
           the service and the dates fall off the right edge, which is exactly the
           half somebody checks a claim against.

           `visibility`, not `display`, on the hidden half: display:none on a
           parent collapses the report with it, since the report is INSIDE the
           screen it is hiding. */}
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 11mm; }
          body * { visibility: hidden !important; }
          #mf-claim-report, #mf-claim-report * { visibility: visible !important; }
          #mf-claim-report {
            position: absolute !important; left: 0 !important; top: 0 !important;
            width: 100% !important; max-width: none !important;
            max-height: none !important; overflow: visible !important;
            box-shadow: none !important; border-radius: 0 !important;
            border: 0 !important; margin: 0 !important;
          }
          .mf-no-print { display: none !important; }
          #mf-claim-report tr { page-break-inside: avoid; }
          #mf-claim-report thead { display: table-header-group; }
          #mf-claim-report .mf-rule { page-break-inside: avoid; }
          #mf-claim-report .mf-rule + .mf-rule { page-break-before: auto; }
        }
        @keyframes mf-rep-spin { to { transform: rotate(360deg); } }
        .mf-rep-spin { animation: mf-rep-spin .9s linear infinite; }
      `}</style>

      <div id="mf-claim-report"
        style={{
          width: '100%', maxWidth: 1180, background: '#fff', borderRadius: 12,
          border: `1px solid ${BRAND.line}`, boxShadow: '0 24px 60px rgba(15,31,61,.28)',
          overflow: 'hidden',
        }}>

        {/*  ── THE MASTHEAD ────────────────────────────────────────────────
             Carries the subject on the PAGE, not only in the window. A printed
             sheet that does not say which facility and month it is about is a
             sheet nobody can file. */}
        <div style={{
          background: BRAND.navy, color: '#fff', padding: '14px 20px',
          display: 'flex', alignItems: 'flex-start', gap: 14,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '.12em',
              textTransform: 'uppercase', opacity: .75,
            }}>
              {PRODUCT.vendor} {PRODUCT.name}{NBSP}·{NBSP}NHIS Claims
            </div>
            <div style={{ fontSize: 19, fontWeight: 800, marginTop: 2, letterSpacing: '-.01em' }}>
              Claim Error Report
            </div>
            <div style={{ fontSize: 12, marginTop: 3, opacity: .92 }}>
              {title || 'All claims in view'}
            </div>
            {/*  ── EVERY NARROWING IN FORCE, ON THE PAGE ────────────────────
                 A report over 321 in-patient claims and one over the month's 3,725
                 look identical once printed, and the figure gets quoted as the
                 month's. So each filter that shaped this list is named here. */}
            <div style={{ fontSize: 10.5, marginTop: 4, opacity: .7 }}>
              Generated {stamp}
              {scope.narrowing ? `${NBSP}·${NBSP}${scope.narrowing}` : ''}
              {scope.dates ? `${NBSP}·${NBSP}visits ${scope.dates}` : ''}
              {scope.search ? `${NBSP}·${NBSP}search “${scope.search}”` : ''}
            </div>
          </div>

          <div className="mf-no-print" style={{ display: 'flex', gap: 7, flexShrink: 0 }}>
            <button type="button" onClick={() => window.print()}
              title="Print this report, or save it as a PDF"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
                font: 'inherit', fontSize: 12, fontWeight: 700, color: '#fff',
                background: 'rgba(255,255,255,.14)',
                border: '1px solid rgba(255,255,255,.3)',
              }}>
              <Printer size={13} /> Print
            </button>
            <button type="button" onClick={exportExcel} disabled={busy}
              title="Download every finding as a spreadsheet"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 12px', borderRadius: 8,
                cursor: busy ? 'default' : 'pointer',
                font: 'inherit', fontSize: 12, fontWeight: 700,
                color: busy ? 'rgba(255,255,255,.7)' : BRAND.navy,
                background: busy ? 'rgba(255,255,255,.18)' : '#fff',
                border: '1px solid rgba(255,255,255,.5)',
              }}>
              {busy
                ? <><Loader2 size={13} className="mf-rep-spin" /> Building…</>
                : <><FileSpreadsheet size={13} /> Excel</>}
            </button>
            <button type="button" onClick={onClose} aria-label="Close the report"
              style={{
                display: 'grid', placeItems: 'center', width: 32, height: 32,
                borderRadius: 8, cursor: 'pointer', color: '#fff',
                background: 'rgba(255,255,255,.1)',
                border: '1px solid rgba(255,255,255,.24)',
              }}>
              <X size={15} />
            </button>
          </div>
        </div>

        {xlsErr && (
          <div className="mf-no-print" style={{
            padding: '8px 20px', background: BRAND.dangerSoft,
            color: BRAND.danger, fontSize: 12, fontWeight: 700,
          }}>{xlsErr}</div>
        )}

        <div style={{ padding: '16px 20px 20px' }}>

          {/* ══ 1. THE OVERVIEW ═══════════════════════════════════════════ */}
          <div style={{
            display: 'grid', gap: 9, marginBottom: 14,
            gridTemplateColumns: 'repeat(auto-fit, minmax(132px, 1fr))',
          }}>
            {[
              ['Claims in scope', num(model.total), BRAND.ink, null],
              ['Checked', num(model.checked), BRAND.ink,
                model.unchecked ? `${num(model.unchecked)} typed-in not checked` : null],
              ['Will be rejected', num(model.withErrors), BRAND.danger,
                model.checked ? `${pct(model.withErrors, model.checked)} of checked` : null],
              ['Worth checking', num(model.warnOnly), BRAND.amber, 'warnings only'],
              //  ── WHY THIS NUMBER DOES NOT MOVE WHEN A NOTE IS ADDED ──────
              //  A claim carrying only notes is clean, because nothing is against
              //  it. The sub-line says so when there are any, so an officer
              //  reading a row for 1,900 claims and a figure that did not budge
              //  can see both statements are true rather than doubting one.
              ['Nothing against them', num(model.clean), BRAND.green,
                model.checked
                  ? `${pct(model.clean, model.checked)} of checked`
                    + (model.noteOnly ? ` · ${num(model.noteOnly)} carry a note` : '')
                  : null],
              ['Findings', num(model.findings), BRAND.inkSoft,
                model.noteFindings
                  ? `across all rules · ${num(model.noteFindings)} notes besides`
                  : 'across all rules'],
            ].map(([label, value, colour, note]) => (
              <div key={label} style={{
                border: `1px solid ${BRAND.line}`, borderRadius: 9, padding: '9px 11px',
                background: '#fff',
              }}>
                <div style={{
                  fontSize: 21, fontWeight: 800, lineHeight: 1.05, color: colour,
                  fontVariantNumeric: 'tabular-nums',
                }}>{value}</div>
                <div style={{
                  fontSize: 10, fontWeight: 800, color: BRAND.inkMuted, marginTop: 3,
                  textTransform: 'uppercase', letterSpacing: '.03em',
                }}>{label}</div>
                {note && (
                  <div style={{ fontSize: 10, color: BRAND.inkFaint, marginTop: 2 }}>
                    {note}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/*  ── THE ERROR RATE, AS ONE BAR ───────────────────────────────
               The figure the officer is judged on. A bar rather than a fourth
               number because the shape of the month is the point: mostly-green
               with a red sliver is a different month from half-and-half, and two
               percentages side by side do not say that. */}
          {model.checked > 0 && (
            <div style={{
              border: `1px solid ${BRAND.line}`, borderRadius: 9, padding: '10px 12px',
              marginBottom: 16, background: '#fff',
            }}>
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 7,
                flexWrap: 'wrap',
              }}>
                <span style={{
                  fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
                  letterSpacing: '.04em', color: BRAND.inkMuted,
                }}>Error rate</span>
                <span style={{
                  fontSize: 17, fontWeight: 800, color: BRAND.danger,
                  fontVariantNumeric: 'tabular-nums',
                }}>{pct(model.withErrors, model.checked)}</span>
                <span style={{ fontSize: 11, color: BRAND.inkFaint }}>
                  {num(model.withErrors)} of {num(model.checked)} checked claims carry at
                  least one error NHIA rejects
                </span>
              </div>
              <div style={{
                display: 'flex', height: 9, borderRadius: 20, overflow: 'hidden',
                background: BRAND.head, border: `1px solid ${BRAND.line}`,
              }}>
                {[
                  [model.withErrors, BRAND.danger, 'will be rejected'],
                  [model.warnOnly, BRAND.warning, 'worth checking'],
                  [model.clean, BRAND.green, 'nothing against them'],
                ].filter(([n]) => n > 0).map(([n, colour, label]) => (
                  <div key={label} title={`${num(n)} ${label}`}
                    style={{ width: `${(n / model.checked) * 100}%`, background: colour }} />
                ))}
              </div>
            </div>
          )}

          {/* ══ 2. THE SUMMARY ════════════════════════════════════════════ */}
          {model.rules.length === 0 ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 9, padding: '14px 16px',
              borderRadius: 9, background: BRAND.greenSoft,
              border: `1px solid ${BRAND.green}44`, color: BRAND.greenDark,
              fontSize: 13, fontWeight: 700,
            }}>
              <CheckCircle2 size={17} />
              {model.checked === 0
                ? <span>None of these claims could be checked by these rules
                    {model.unchecked ? ' — they are all typed-in claims, whose details live in different tables' : ''}.</span>
                : <span>No rule fired on any of the {num(model.checked)} claims checked.
                    Nothing here needs correcting before submission.</span>}
            </div>
          ) : (
            <>
              <SectionHeading n="1" text="What is wrong, and how much of it" />
              <table style={{
                width: '100%', borderCollapse: 'collapse', marginBottom: 18,
                border: `1px solid ${BRAND.line}`, borderRadius: 8,
              }}>
                <thead>
                  <tr>
                    <th style={{ ...th, width: 28, textAlign: 'right' }}>#</th>
                    <th style={th}>What is wrong</th>
                    <th style={{ ...th, width: 88 }}>Severity</th>
                    <th style={{ ...th, width: 74, textAlign: 'right' }}>Claims</th>
                    <th style={{ ...th, width: 130 }}>Share of checked</th>
                  </tr>
                </thead>
                <tbody>
                  {model.rules.map((r, i) => (
                    <tr key={r.code}>
                      <td style={{ ...td, textAlign: 'right', color: BRAND.inkFaint }}>
                        {i + 1}
                      </td>
                      <td style={{ ...td, whiteSpace: 'normal', fontWeight: 700, color: BRAND.ink }}>
                        {r.name}
                      </td>
                      <td style={td}>{sevChip(r.severity)}</td>
                      <td style={{
                        ...td, textAlign: 'right', fontWeight: 800, color: BRAND.ink,
                        fontVariantNumeric: 'tabular-nums',
                      }}>{num(r.rows.length)}</td>
                      <td style={td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <div style={{
                            flex: 1, height: 6, borderRadius: 20, background: BRAND.head,
                            overflow: 'hidden', minWidth: 46,
                          }}>
                            <div style={{
                              width: `${model.checked
                                ? (r.rows.length / model.checked) * 100 : 0}%`,
                              height: '100%',
                              //  Grey for a note, matching its chip: the longest
                              //  bar in the table must not be the loudest colour
                              //  when nothing is wrong with those claims.
                              background: r.severity === 'error' ? BRAND.danger
                                : r.severity === 'note' ? BRAND.inkFaint
                                  : BRAND.warning,
                            }} />
                          </div>
                          <span style={{
                            fontVariantNumeric: 'tabular-nums', color: BRAND.inkMuted,
                            fontSize: 10.5, minWidth: 40, textAlign: 'right',
                          }}>{pct(r.rows.length, model.checked)}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* ══ WHAT THE CLAIMS ARE MISSING, ONE BY ONE ═════════════════
                   THE HOLE THIS FILLS. An officer filtered the month to "No
                   medication · 735 claims", opened this report, and found no
                   mention of it anywhere — the report is built from what the rows
                   carry, and a GAP was a search predicate and a tile count and
                   nothing else. The tile said 735 and the report said nothing,
                   about the same claims, on the same screen.

                   ITS OWN TABLE, NOT THE ONE ABOVE. A gap is something the claim
                   is MISSING; a rule is something it gets WRONG. NHIA rejects the
                   second and does not reject the first — an out-patient
                   consultation with no medicine is an ordinary claim, and there
                   are 735 of those here against 3 real errors. Merged in, the
                   error rate goes from 0.4% to 100% and the one number the
                   officer acts on stops meaning anything. */}
              {/*  THE SECTION NOW APPEARS WHEN THE CHECKS FAILED TOO, saying so.
                   It used to render only when something fired, so a batch whose
                   completeness query had thrown looked identical to a complete
                   one — no section, nothing said. See `gapsChecked`. */}
              {!model.gapsChecked && (
                <>
                  <SectionHeading n="2" text="What the claims are missing" />
                  <div style={{
                    display: 'flex', gap: 10, padding: '10px 12px', marginBottom: 18,
                    border: `1px solid ${BRAND.amberLine}`, borderRadius: 8,
                    background: BRAND.amberSoft, breakInside: 'avoid',
                  }}>
                    <AlertTriangle size={15}
                      style={{ color: BRAND.amber, flexShrink: 0, marginTop: 1 }} />
                    <div style={{ fontSize: 12, lineHeight: 1.6, color: BRAND.ink }}>
                      <b>The completeness checks could not be run on this batch.</b>
                      <div style={{ color: BRAND.inkMuted, marginTop: 3 }}>
                        This is not an all-clear — nothing was asked. It usually means
                        this database is missing a column one of the checks reads, so
                        the whole set failed together. Everything above is unaffected.
                      </div>
                    </div>
                  </div>
                </>
              )}

              {model.gapsChecked && model.gapRules.length > 0 && (
                <>
                  <SectionHeading n="2" text="What the claims are missing" />
                  <div style={{
                    fontSize: 11, color: BRAND.inkMuted, marginBottom: 10, lineHeight: 1.55,
                  }}>
                    Not errors — NHIA does not reject a claim for these, and they are
                    deliberately left out of the error rate above. They are what the
                    claims do not carry, and each is worth a look before the month is
                    submitted: a drug given and never billed is money the facility has
                    spent and not claimed.
                  </div>
                  <table style={{
                    width: '100%', borderCollapse: 'collapse', marginBottom: 18,
                    border: `1px solid ${BRAND.line}`, borderRadius: 8,
                  }}>
                    <thead>
                      <tr>
                        <th style={{ ...th, width: 28, textAlign: 'right' }}>#</th>
                        <th style={th}>What is missing</th>
                        <th style={{ ...th, width: 74, textAlign: 'right' }}>Claims</th>
                        <th style={{ ...th, width: 130 }}>Share of checked</th>
                      </tr>
                    </thead>
                    <tbody>
                      {model.gapRules.map((r, i) => (
                        <tr key={r.code}>
                          <td style={{ ...td, textAlign: 'right', color: BRAND.inkFaint }}>
                            {i + 1}
                          </td>
                          <td style={{
                            ...td, whiteSpace: 'normal', fontWeight: 700, color: BRAND.ink,
                          }}>{r.name}</td>
                          <td style={{
                            ...td, textAlign: 'right', fontWeight: 800, color: BRAND.ink,
                            fontVariantNumeric: 'tabular-nums',
                          }}>{num(r.rows.length)}</td>
                          <td style={td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                              <div style={{
                                flex: 1, height: 6, borderRadius: 20, background: BRAND.head,
                                overflow: 'hidden', minWidth: 46,
                              }}>
                                <div style={{
                                  width: `${model.checked
                                    ? (r.rows.length / model.checked) * 100 : 0}%`,
                                  height: '100%', background: BRAND.inkFaint,
                                }} />
                              </div>
                              <span style={{
                                fontVariantNumeric: 'tabular-nums', color: BRAND.inkMuted,
                                fontSize: 10.5, minWidth: 40, textAlign: 'right',
                              }}>{pct(r.rows.length, model.checked)}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {/*  ── THE TOTALS, WHICH ARE TWO DIFFERENT NUMBERS ─────────
                         "Give me the right totals of the percentage."

                         The column SUM is not a claim count. A claim with no
                         diagnosis and no medication appears in two rows, so adding
                         the column up counts it twice — which is right for "how
                         much work is there" and wrong for "how many claims". Both
                         are shown and each is labelled as what it is, because
                         quoting one for the other is how a report overstates a
                         month.

                         The share is computed off the DISTINCT figure, since that
                         is the only one that can be a percentage of the claims
                         checked — the sum can exceed 100% and a bar that overflows
                         its track says nothing. */}
                    <tfoot>
                      <tr>
                        <td style={{ ...td, borderTop: `2px solid ${BRAND.line}` }} />
                        <td style={{
                          ...td, borderTop: `2px solid ${BRAND.line}`,
                          whiteSpace: 'normal', color: BRAND.inkMuted,
                        }}>
                          <b style={{ color: BRAND.ink }}>
                            {num(model.withAnyGap)} claim
                            {model.withAnyGap === 1 ? '' : 's'} missing something
                          </b>
                          {' — '}
                          {num(model.gapFindings)} entr
                          {model.gapFindings === 1 ? 'y' : 'ies'} above, because a
                          claim missing two things is counted in both rows.
                        </td>
                        <td style={{
                          ...td, borderTop: `2px solid ${BRAND.line}`,
                          textAlign: 'right', fontWeight: 800, color: BRAND.ink,
                          fontVariantNumeric: 'tabular-nums',
                        }}>{num(model.withAnyGap)}</td>
                        <td style={{
                          ...td, borderTop: `2px solid ${BRAND.line}`,
                          fontWeight: 800, color: BRAND.ink,
                          fontVariantNumeric: 'tabular-nums',
                        }}>{pct(model.withAnyGap, model.checked)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </>
              )}

              {/* ══ WHAT THE WHOLE BATCH IS MISSING ═════════════════════════
                   Not per-claim faults, so not in the table above: each of these
                   is true of essentially every claim in the batch, and writing
                   them as findings would add tens of thousands of rows and bury
                   the ones an officer can act on. One sentence each instead.

                   Omitted entirely when there is nothing to say. */}
              {model.batchGaps.length > 0 && (
                <>
                  {/*  Section 2 is now taken whenever the per-claim block showed
                       ANYTHING — its rows, or the note saying the checks could not
                       run. Keyed on both, or a failed batch numbered its sections
                       2 and 2. */}
                  <SectionHeading
                    n={(!model.gapsChecked || model.gapRules.length > 0) ? '3' : '2'}
                    text="What this whole batch is missing" />
                  <div style={{
                    fontSize: 11, color: BRAND.inkMuted, marginBottom: 10, lineHeight: 1.55,
                  }}>
                    These are true of the batch rather than of particular claims, so they
                    are not counted as findings above. They are worth reading before the
                    file goes to NHIA.
                  </div>
                  <div style={{ display: 'grid', gap: 8, marginBottom: 18 }}>
                    {model.batchGaps.map((g) => (
                      <div key={g.key} style={{
                        display: 'flex', gap: 10, padding: '10px 12px',
                        border: `1px solid ${BRAND.amberLine}`, borderRadius: 8,
                        background: BRAND.amberSoft, breakInside: 'avoid',
                      }}>
                        <AlertTriangle size={15} style={{ color: BRAND.amber, flexShrink: 0, marginTop: 1 }} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 800, color: BRAND.amber }}>
                            {g.say}
                          </div>
                          <div style={{
                            fontSize: 11.5, color: BRAND.inkMuted, lineHeight: 1.55, marginTop: 3,
                          }}>
                            {g.why}
                          </div>
                          <div style={{
                            fontSize: 10.5, color: BRAND.inkFaint, marginTop: 4,
                            fontVariantNumeric: 'tabular-nums',
                          }}>
                            {/*  COUNTED OVER THE BATCH, not over what the screen is
                                 filtered to — the server counts these across the
                                 whole month, and pairing that numerator with the
                                 report's filtered denominator printed nonsense
                                 like "16,138 of 3,725". */}
                            {num(g.n)} of {num(g.of)} claims in this batch
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* ══ THE DETAIL ═════════════════════════════════════════════ */}
              <SectionHeading
                n={String(2 + (model.gapRules.length > 0 ? 1 : 0)
                  + (model.batchGaps.length > 0 ? 1 : 0))}
                text="The claims themselves" />
              <div style={{
                fontSize: 11, color: BRAND.inkMuted, marginBottom: 10, lineHeight: 1.55,
              }}>
                One block per check, worst first — the errors, then the warnings, then
                what the claims are missing. A claim appearing under several checks is
                listed under each, which is why the {num(model.findings
                  + model.gapFindings)} findings below exceed the {num(
                  model.withErrors + model.warnOnly + model.withAnyGap)} claims they
                belong to.
              </div>

              {/*  Errors and warnings first, then the gaps — one list, so a gap
                   block renders with exactly the same code and the same columns as
                   a rule block rather than a near-copy that drifts. */}
              {[...model.rules, ...model.gapRules].map((r, i) => {
                const shown = r.rows.slice(0, DETAIL_CAP);
                const hidden = r.rows.length - shown.length;
                return (
                  <div key={r.code} className="mf-rule" style={{ marginBottom: 16 }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
                      flexWrap: 'wrap',
                    }}>
                      {r.severity === 'error'
                        ? <AlertCircle size={14} style={{ color: BRAND.danger, flexShrink: 0 }} />
                        : r.severity === 'gap'
                          //  Not a warning triangle. These claims are not suspected
                          //  of anything — they simply do not carry the thing named.
                          ? <FileText size={14} style={{ color: BRAND.inkFaint, flexShrink: 0 }} />
                          : <AlertTriangle size={14} style={{ color: BRAND.warning, flexShrink: 0 }} />}
                      <span style={{ fontSize: 13, fontWeight: 800, color: BRAND.ink }}>
                        {i + 1}. {r.name}
                      </span>
                      {sevChip(r.severity)}
                      <span style={{
                        fontSize: 11.5, color: BRAND.inkMuted, fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                        {num(r.rows.length)} claim{r.rows.length === 1 ? '' : 's'}
                        {' · '}{pct(r.rows.length, model.checked)} of checked
                      </span>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                      <table style={{
                        width: '100%', borderCollapse: 'collapse',
                        border: `1px solid ${BRAND.line}`,
                      }}>
                        <thead>
                          <tr>
                            <th style={{ ...th, width: 30, textAlign: 'right' }}>#</th>
                            {COLUMNS.map((c) => <th key={c.key} style={th}>{c.head}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {shown.map((c, j) => (
                            <tr key={`${c.source}-${c.id}`}
                              style={{ background: j % 2 ? '#FBFCFE' : '#fff' }}>
                              <td style={{ ...td, textAlign: 'right', color: BRAND.inkFaint }}>
                                {j + 1}
                              </td>
                              {COLUMNS.map((col) => (
                                <td key={col.key} style={{
                                  ...td,
                                  //  The patient is what the row is ABOUT, so it is the
                                  //  one column that carries weight.
                                  fontWeight: col.key === 'name' ? 700 : 400,
                                  color: col.key === 'name' ? BRAND.ink : BRAND.inkSoft,
                                  //  Identifiers line up when they are tabular, and a
                                  //  column of membership numbers is read by comparing
                                  //  them down the page.
                                  fontVariantNumeric: /no$|ccc/.test(col.key)
                                    ? 'tabular-nums' : 'normal',
                                  maxWidth: col.key === 'service' ? 260 : undefined,
                                  whiteSpace: col.key === 'service' ? 'normal' : 'nowrap',
                                }}>{cell(c, col.key)}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {hidden > 0 && (
                      <div style={{
                        fontSize: 11, color: BRAND.amber, background: BRAND.amberSoft,
                        border: `1px solid ${BRAND.amberLine}`, borderTop: 0,
                        padding: '6px 9px', lineHeight: 1.5,
                      }}>
                        {num(hidden)} more claim{hidden === 1 ? '' : 's'} not listed here —
                        the first {num(DETAIL_CAP)} are shown so the page stays workable.
                        <strong> The Excel export carries all {num(r.rows.length)}.</strong>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {/*  ── WHAT THIS REPORT DOES NOT COVER ───────────────────────────
               On the page, not in a tooltip. A report is read by somebody who
               cannot ask it questions, so its limits have to travel with it. */}
          <div style={{
            marginTop: 14, padding: '9px 11px', borderRadius: 8,
            background: BRAND.navySoft, border: `1px solid ${BRAND.line}`,
            fontSize: 10.5, color: BRAND.inkSoft, lineHeight: 1.6,
            display: 'flex', gap: 8,
          }}>
            <HelpCircle size={13} style={{ flexShrink: 0, marginTop: 2, color: BRAND.navy }} />
            <span>
              Rates are over the <strong>checked</strong> claims. A typed-in claim is not
              checked by these rules — its diagnoses and drugs live in different tables —
              so it is counted separately and never as clean.
              {model.unchecked > 0 && (
                <> {num(model.unchecked)} of the {num(model.total)} claims in scope
                  {' '}{model.unchecked === 1 ? 'is' : 'are'} in that position.</>
              )}
              {' '}<strong>Rejects</strong> means NHIA returns the claim;
              {' '}<strong>Check</strong> means it is worth a look but will not be sent back.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHeading({ n, text }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
      paddingBottom: 5, borderBottom: `2px solid ${BRAND.navy}`,
    }}>
      <span style={{
        display: 'grid', placeItems: 'center', width: 18, height: 18,
        borderRadius: 5, background: BRAND.navy, color: '#fff',
        fontSize: 10.5, fontWeight: 800, flexShrink: 0,
      }}>{n}</span>
      <span style={{
        fontSize: 11.5, fontWeight: 800, textTransform: 'uppercase',
        letterSpacing: '.05em', color: BRAND.navy,
      }}>{text}</span>
    </div>
  );
}
